<script setup>
/* global axios */
// Shell do painel de configuração de nó (direita). Carrega dados auxiliares (funis/agentes/
// times/labels/SLA), provê via inject e renderiza o componente de config do tipo selecionado.
import { computed, reactive, provide, onMounted, toRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { nodeMeta, nodeColor } from '../flowNodeTypes';
import { configComponent } from './config/index';

const props = defineProps({
  node: { type: Object, default: null },
  trigger: { type: Object, default: () => ({}) },
  accountId: { type: [String, Number], required: true },
  flowType: { type: String, default: 'chatbot' },
});

const emit = defineEmits(['delete']);

const { t } = useI18n();

const type = computed(() => props.node?.data?.nodeType);
const meta = computed(() => (type.value ? nodeMeta(type.value) : null));
const color = computed(() => (type.value ? nodeColor(type.value) : null));
const comp = computed(() => (type.value ? configComponent(type.value) : null));
const nodeData = computed(() => props.node?.data);
const updateTitle = e => {
  if (nodeData.value) nodeData.value.title = e.target.value;
};

// dados auxiliares compartilhados com todos os configs via inject
const aux = reactive({
  accountId: props.accountId,
  flowType: props.flowType,
  funnels: [],
  agents: [],
  teams: [],
  labels: [],
  slaPolicies: [],
  knowledgeBases: [],
});
provide('flowAux', aux);
provide('flowTrigger', toRef(props, 'trigger'));

const base = `/api/v1/accounts/${props.accountId}`;
const loadAux = async () => {
  try {
    aux.funnels = (await axios.get(`${base}/kanban/funnels`)).data.data || [];
  } catch (e) {
    /* noop */
  }
  try {
    aux.agents = (await axios.get(`${base}/agents`)).data || [];
  } catch (e) {
    /* noop */
  }
  try {
    aux.teams = (await axios.get(`${base}/teams`)).data || [];
  } catch (e) {
    /* noop */
  }
  try {
    aux.labels = (await axios.get(`${base}/labels`)).data.payload || [];
  } catch (e) {
    /* noop */
  }
  try {
    aux.slaPolicies =
      (await axios.get(`${base}/sla_policies`)).data.payload ||
      (await axios.get(`${base}/sla_policies`)).data ||
      [];
  } catch (e) {
    /* noop */
  }
  try {
    aux.knowledgeBases =
      (await axios.get(`${base}/kanban/knowledge_bases`)).data.data || [];
  } catch (e) {
    /* noop */
  }
};

onMounted(loadAux);
</script>

<template>
  <div class="p-4">
    <div v-if="!node" class="py-16 text-center text-n-slate-10">
      <p class="text-sm">{{ t('CHATBOT.nodePanel.selectNode') }}</p>
      <p class="mt-1 text-xs">{{ t('CHATBOT.nodePanel.selectNodeHint') }}</p>
    </div>

    <template v-else>
      <div class="flex items-center justify-between mb-3">
        <span class="flex items-center gap-2">
          <svg
            class="w-5 h-5"
            :style="{ color: color.accent }"
            viewBox="0 0 24 24"
            :fill="meta.fill ? 'currentColor' : 'none'"
            :stroke="meta.fill ? 'none' : 'currentColor'"
            stroke-width="2"
          >
            <path
              v-for="(p, i) in meta.icon"
              :key="i"
              stroke-linecap="round"
              stroke-linejoin="round"
              :d="p"
            />
          </svg>
          <h3 class="text-heading-3 text-n-slate-12">{{ t(meta.label) }}</h3>
        </span>
        <button
          class="inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-n-alpha-1 text-n-ruby-11"
          :title="t('CHATBOT.nodes.common.delete')"
          @click="emit('delete', node.id)"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            class="w-4 h-4"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2m2 0v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V6"
            />
          </svg>
        </button>
      </div>

      <label class="block mb-1 text-sm font-medium text-n-slate-12">{{
        t('CHATBOT.nodePanel.nodeNameLabel')
      }}</label>
      <input
        :value="node.data.title || ''"
        class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 mb-3"
        :placeholder="t('CHATBOT.nodePanel.nodeNamePlaceholder')"
        @input="updateTitle"
      />

      <component :is="comp" :key="node.id" :node="node" />
    </template>
  </div>
</template>
